import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import {
	clearPpwrEmCountryExclusions,
	searchPpwrEmCountryExclusionsRequest,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrEmCountryExclusions,
	getPpwrEmCountryExclusionsLoading,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrEmCountryExclusion } from 'redux/reducers/ppwr/types';
import { EDIT_ACTION, tableStructure } from './tableStructure';
import { CountryExclusionDialog } from './CountryExclusionDialog';

/** An EM is excluded in only a few countries, so the widget shows all of them on one page. */
const COUNTRY_EXCLUSIONS_PAGE_SIZE = 1000;

type TCountryExclusionsProps = {
	emId: number;
};

/** The dialog edits the given exclusion, or adds a new one when it is `null`. */
type TDialogState = { open: false } | { open: true; exclusion: TPpwrEmCountryExclusion | null };

/** "Country Exclusions" widget: the countries the EM is excluded from PPWR reporting in. */
export const CountryExclusions: FC<TCountryExclusionsProps> = ({ emId }) => {
	const dispatch = useDispatch();
	const exclusions = useSelector(getPpwrEmCountryExclusions);
	const loading = useSelector(getPpwrEmCountryExclusionsLoading);
	const [dialog, setDialog] = useState<TDialogState>({ open: false });

	useEffect(() => {
		if (!Number.isFinite(emId)) return undefined;
		dispatch(
			searchPpwrEmCountryExclusionsRequest({
				pageNumber: 1,
				pageSize: COUNTRY_EXCLUSIONS_PAGE_SIZE,
				filters: [{ fieldName: 'emId', value: String(emId), operator: '=' }],
			}),
		);
		return () => {
			dispatch(clearPpwrEmCountryExclusions());
		};
	}, [dispatch, emId]);

	const excludedCountryIds = useMemo(
		() => (exclusions?.data ?? []).map((exclusion) => exclusion.countryId),
		[exclusions],
	);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== EDIT_ACTION) return;
			const exclusion = exclusions?.data?.find((item) => item.id === id);
			if (exclusion) setDialog({ open: true, exclusion });
		},
		[exclusions],
	);

	const handleDialogClose = useCallback(() => {
		setDialog({ open: false });
	}, []);

	return (
		<>
			{dialog.open && (
				<CountryExclusionDialog
					emId={emId}
					exclusion={dialog.exclusion}
					excludedCountryIds={excludedCountryIds}
					onClose={handleDialogClose}
				/>
			)}
			<EasTableWithoutFilter<TPpwrEmCountryExclusion>
				tableStructure={tableStructure}
				items={exclusions}
				itemsIsLoading={loading}
				handleAction={handleAction}
				skeletonRows={[1, 2, 3]}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="body1" color="primary" fontWeight="bold">
							Country Exclusions
						</Typography>
						{loading && <CircularProgress size={20} />}
					</Box>
					<Button
						variant="contained"
						size="small"
						startIcon={<Add />}
						onClick={() => setDialog({ open: true, exclusion: null })}
						disabled={!Number.isFinite(emId)}
					>
						Add Country Exclusion
					</Button>
				</Box>
			</EasTableWithoutFilter>
		</>
	);
};
